import { useCallback } from 'react';
import { commitLocalUpdate } from 'react-relay';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type {
	JiraIssueExpandedGroup,
	JiraIssueExpandedParent,
} from '@atlassian/jira-relay/src/__generated__/useGetRealtimeIssueDetailsQuery.graphql.ts';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	useConnectionsList,
	type ConnectionDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsConditionalFormattingEnabled,
	useIsDensityFull,
	useIsEditableSyncLoaded,
	useIsHideDoneItemsEnabled,
	useIsInlineEditingEnabled,
	useIsRelationshipFieldEnabled,
	useIsTimelineEnabled,
} from '../../controllers/features/selectors.tsx';
import { useIssueFetchQueryVariables } from '../../controllers/issue-fetch-query-variables/index.tsx';
import {
	useIssueRealtimeUpdater,
	type GroupDetails,
} from '../../controllers/issue-realtime-updater/index.tsx';
import { useGetRealtimeIssueDetails } from '../../services/realtime-issue-details/useGetRealtimeIssueDetails.tsx';
import type { SearchViewContextInputType } from '../../services/realtime-issue-details/types.tsx';
import { useGetGroupsForIssues } from '../../services/groups-for-issues/useGetGroupsForIssues.tsx';

export const generateSearchViewContextInput = ({
	connections,
	isGroupingEnabled,
	groupedByFieldId,
}: {
	connections: Record<string, ConnectionDetails>;
	isGroupingEnabled: boolean;
	groupedByFieldId?: string | null;
}): SearchViewContextInputType => {
	const expandedParents: JiraIssueExpandedParent[] = [];
	const groups: JiraIssueExpandedGroup[] = [];

	let topLevelItemsLoaded = 0;
	Object.values(connections).forEach((connection) => {
		if (connection.type === 'PARENT_CHILDREN') {
			expandedParents.push({ parentIssueId: connection.parentId, first: connection.first });
		} else if (connection.type === 'GROUP_CHILDREN') {
			groups.push({
				jql: connection.jql,
				fieldValue: connection.fieldValue,
				first: connection.first,
			});
		} else if (connection.type === 'ROOT_ISSUES') {
			topLevelItemsLoaded = connection.first;
		}
	});

	if (isGroupingEnabled && groupedByFieldId) {
		return { expandedGroups: { groupedByFieldId, groups } };
	}

	return { expandedParents, topLevelItemsLoaded };
};

export const useRealtimeUpdatesForIssues = () => {
	const environment = getRelayEnvironment();
	const cloudId = useCloudId();
	const { fieldSetIds } = useIssueFetchQueryVariables();
	const isDensityFull = useIsDensityFull();
	const isInlineEditingEnabled = useIsInlineEditingEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isLinkRelationshipFieldEnabled = useIsRelationshipFieldEnabled();
	const isEditableSyncLoaded = useIsEditableSyncLoaded();
	const { connections } = useConnectionsList();
	const getGroupsForIssues = useGetGroupsForIssues();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getRealtimeIssueDetails = useGetRealtimeIssueDetails();
	const { updateIssuesOnConnection, groupsForIssueUpdater, updateNewParentsOnConnection } =
		useIssueRealtimeUpdater();

	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		useIsConditionalFormattingEnabled();

	return useCallback(
		async ({
			issues,
			jql,
			groupByFieldId,
			numGroupsLoaded,
			groupConnectionId,
			isHierarchyEnabled,
			projectKey,
			isStatusTrackingEnabled,
		}: {
			issues: { issueAri: string; issueId: string }[];
			jql: string;
			groupByFieldId: string | undefined;
			groupConnectionId: string | undefined;
			numGroupsLoaded: number;
			isHierarchyEnabled: boolean;
			projectKey: string;
			isStatusTrackingEnabled: boolean;
		}) => {
			const isGroupingEnabled = !!groupByFieldId;
			const searchViewContextInput = generateSearchViewContextInput({
				connections,
				isGroupingEnabled,
				groupedByFieldId: groupByFieldId,
			});

			const { issues: updatedIssues, searchViewContexts } = await getRealtimeIssueDetails({
				issues,
				cloudId,
				fieldSetIds,
				isInlineEditingEnabled,
				isDensityFull,
				isHierarchyEnabled,
				isGroupingEnabled,
				isHideDoneItemsEnabled,
				issueSearchInput: {
					jql,
				},
				searchViewContextInput,
				projectKey,
				isStatusTrackingEnabled,
				isConditionalFormattingEnabled,
				isTimelineEnabled,
				isLinkRelationshipFieldEnabled,
				isEditableSyncLoaded,
			});

			let groupsToInsert: GroupDetails[] | undefined;
			const shouldQueryGroupsForIssues = isGroupingEnabled && !!groupByFieldId;
			if (shouldQueryGroupsForIssues) {
				const issueKeysWithContext = new Set(
					searchViewContexts
						?.flatMap((connection) => {
							return connection.contexts?.map((context) => context?.sourceIssueId);
						})
						.filter(Boolean),
				);

				const issueChangesWithoutContext = fg('group_by_any_field')
					? updatedIssues?.map((issue) => ({ issueId: issue.issueId }))
					: updatedIssues?.reduce<{ issueId: string }[]>((acc, issue) => {
							if (issue && !issueKeysWithContext.has(issue.issueId ?? '')) {
								acc.push({ issueId: issue.issueId });
							}
							return acc;
						}, []);

				groupsToInsert = await getGroupsForIssues({
					issueChanges: issueChangesWithoutContext,
					fieldId: groupByFieldId,
					numGroupsLoaded,
					issueSearchInput: { jql },
					staticViewInput: {
						isHierarchyEnabled,
						isGroupingEnabled,
						isHideDoneEnabled: isHideDoneItemsEnabled,
					},
				});
			}

			const eventDetails = {
				issuesUnmoved: new Set<string>(),
				issuesUnrelated: new Set<string>(),
				issuesUpdated: new Set<string>(),
			};

			const updatedIssuesMap = new Map(
				updatedIssues?.map(({ issueId, issueRecordId, fieldSetsRecordId }) => [
					issueId,
					{
						issueId,
						issueRecordId,
						fieldSetsRecordId,
					},
				]),
			);

			const updatedParents = new Set<string>(
				updatedIssues.map((issue) => issue.parentIssueRecordId).filter(Boolean),
			);

			commitLocalUpdate(environment, (store) => {
				Object.values(connections).forEach((connection) => {
					updateNewParentsOnConnection({
						store,
						connection,
						updatedParents,
						projectKey,
					});

					const matchingContext = searchViewContexts?.find((context) => {
						if (connection.type === 'PARENT_CHILDREN') {
							return context?.parentIssueId === connection.parentId;
						}
						if (connection.type === 'GROUP_CHILDREN') {
							return context?.jql === connection.jql;
						}
						return context?.jql == null && context?.parentIssueId == null;
					});
					const updatedDetails = updateIssuesOnConnection({
						store,
						connection,
						searchViewContexts: matchingContext?.contexts ?? [],
						updatedIssues: updatedIssuesMap,
						groupConnectionId,
						projectKey,
						hasAccurateGroupCounts: fg('group_by_any_field')
							? connection.type === 'GROUP_CHILDREN' &&
								groupsToInsert?.some((group) => group.id === connection.groupId)
							: undefined,
					});

					updatedDetails.issuesMoved.forEach(
						eventDetails.issuesUpdated.add,
						eventDetails.issuesUpdated,
					);
					updatedDetails.issuesUnmoved.forEach(
						eventDetails.issuesUnmoved.add,
						eventDetails.issuesUnmoved,
					);
					updatedDetails.issuesUnrelated.forEach(
						eventDetails.issuesUnrelated.add,
						eventDetails.issuesUnrelated,
					);
					updatedDetails.issuesRemoved.forEach(
						eventDetails.issuesUpdated.add,
						eventDetails.issuesUpdated,
					);
				});

				if (groupsToInsert && groupsToInsert.length > 0) {
					groupsForIssueUpdater({
						store,
						groupConnectionId,
						groupsForIssue: groupsToInsert,
					});
				}
			});

			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'issueTableRealTimeEventsIssueDetails Processed',
				{
					issuesInSamePosition: eventDetails.issuesUnmoved.size,
					issuesInNewPosition: eventDetails.issuesUpdated.size,
					issuesNotImpactingList: Array.from(eventDetails.issuesUnrelated).reduce(
						(acc, issueId) =>
							eventDetails.issuesUpdated.has(issueId) || eventDetails.issuesUnmoved.has(issueId)
								? acc
								: acc + 1,
						0,
					),
				},
			);
		},
		[
			connections,
			getRealtimeIssueDetails,
			cloudId,
			fieldSetIds,
			isInlineEditingEnabled,
			isDensityFull,
			isHideDoneItemsEnabled,
			environment,
			createAnalyticsEvent,
			getGroupsForIssues,
			updateNewParentsOnConnection,
			updateIssuesOnConnection,
			groupsForIssueUpdater,
			isConditionalFormattingEnabled,
			isTimelineEnabled,
			isLinkRelationshipFieldEnabled,
			isEditableSyncLoaded,
		],
	);
};

export const useRealtimeUpdateOnRefresh = (onRefresh?: () => void) => {
	const { connections } = useConnectionsList();
	const environment = getRelayEnvironment();

	return useCallback(() => {
		// This will reload all expanded parents/groups
		environment.commitUpdate((store) => {
			Object.values(connections).forEach((connection) => {
				const connectionToInvalidate = store.get(connection.connectionId);
				if (connectionToInvalidate) {
					connectionToInvalidate.invalidateRecord();
				}
			});
		});

		onRefresh?.();
	}, [connections, environment, onRefresh]);
};
